'use client';
import { FullListing } from '@th/shared/src/types/FullListing';
import BlurText from '../BlurText';
import textWithFallback from '@th/shared/src/helpers/textWithFallback';
import { OFFER_TOOLTIP_TEXT } from 'packages/frontend-shared/src/constants/tooltip';
import SvgIcon from '../icons/SvgIcon';
import Tooltip from '../Tooltip';
import formatFullDetails from '@th/shared/src/helpers/listings/formatFullDetails';
import { featureFlags } from '../../utils/featureFlags';

type Props = {
  listing: FullListing;
  isBlurred: boolean;
};

export default function ListingFullDetails({ listing, isBlurred }: Props) {
  function Item({
    title,
    value,
    tooltip,
  }: {
    title: string;
    value: string | boolean;
    tooltip?: string;
  }) {
    if (!value) return null;

    const formattedValue = formatFullDetails(value);

    return (
      <div className="">
        <div className="flex flex-row justify-between sm:grid sm:grid-cols-2 lg:grid-cols-3 hover:bg-lightestPrimary flex items-baseline -mx-2 px-2 rounded-lg">
          <div className="col-span-1 flex flex-row items-baseline">
            <p className="my-1">{title}</p>
            {tooltip && (
              <>
                <SvgIcon
                  name="TbInfoCircle"
                  className="ml-2 h-4 w-4 mb-0 text-primary hover:cursor-pointer"
                  data-tooltip-id={`${title}-tooltip`}
                  data-tooltip-content={tooltip}
                />
                <Tooltip id={`${title}-tooltip`} />
              </>
            )}
          </div>
          <BlurText
            isBlurred={isBlurred}
            className="my-1 text-end sm:text-start"
          >
            {formattedValue}
          </BlurText>
        </div>
      </div>
    );
  }

  return (
    <div>
      {featureFlags.SHOW_OFFER_DATES && (
        <Item
          title="Offer Date"
          value={textWithFallback(listing.offerDateText(), 'N/A')}
          tooltip={OFFER_TOOLTIP_TEXT}
        />
      )}
      <Item title="Bedrooms" value={listing.bedroomsText('')} />
      <Item title="Bathrooms" value={listing.bathroomsText('')} />
      <Item title="Garage" value={listing.details.garage} />
      <Item title="Driveway" value={listing.details.driveway} />
      <Item title="Rooms" value={listing.details.numRooms?.toString()} />
      <Item title="Area" value={listing.sqftText()} />
      <Item title="Style" value={listing.style} />
      <Item title="Den" value={listing.details.den} />
      <Item title="Air Conditioning" value={listing.details.airConditioning} />
      <Item title="Balcony" value={listing.details.balcony} />
      <Item title="Central Vac" value={listing.details.centralVac} />
      <Item title="Elevator" value={listing.details.elevator} />
      <Item
        title="Exterior Construction"
        value={listing.details.exteriorConstruction1}
      />
      <Item
        title="Exterior Construction"
        value={listing.details.exteriorConstruction2}
      />
      <Item title="Furnished" value={listing.details.furnished} />
      <Item title="Heating" value={listing.details.heating} />
      <Item title="Fireplaces" value={listing.details.numFireplaces} />
      <Item title="Irregular Lot" value={listing.lot.irregular} />
      <Item title="Swimming Pool" value={listing.poolText()} />
      <Item title="Built" value={listing.yearsOldText()} />
      <Item title="Land Access" value={listing.details.landAccessType} />
      <Item title="Land Sewer" value={listing.details.landSewer} />
      <Item title="View" value={listing.details.viewType} />
      <Item title="Zoning" value={listing.details.zoningDescription} />
      <Item title="Flooring" value={listing.details.flooringType} />
      <Item title="Foundation" value={listing.details.foundationType} />
      <Item title="Landscape" value={listing.details.landscapeFeatures} />
      <Item title="Fire Protection" value={listing.details.fireProtection} />
      <Item title="Roof" value={listing.details.roofMaterial} />
      <Item title="Farm" value={listing.details.farmType} />
      <Item title="Zoning" value={listing.details.zoningType} />
      <Item title="Zoning" value={listing.details.zoning} />
      <Item title="Business" value={listing.details.businessType} />
      <Item title="Business" value={listing.details.businessType} />
      <Item title="Business" value={listing.details.businessSubType} />
      <Item title="Land Disposition" value={listing.details.landDisposition} />
      <Item title="Storage" value={listing.details.storageType} />
      <Item
        title="Construction"
        value={listing.details.constructionStyleSplitLevel}
      />
      <Item title="Construction" value={listing.details.constructionStatus} />
      <Item title="Loading" value={listing.details.loadingType} />
      <Item title="Ceiling" value={listing.details.ceilingType} />
      <Item title="Energuide Rating" value={listing.details.energuideRating} />
      <Item
        title="Energy Certification"
        value={listing.details.energyCertification}
      />
      <Item title="Amperage" value={listing.details.amperage} />
      <Item title="Water Source" value={listing.details.waterSource} />
      <Item title="Laundry Level" value={listing.details.laundryLevel} />
      <Item title="Certification" value={listing.details.certificationLevel} />
      <Item title="Accessible" value={listing.details.handicappedEquipped} />
      <Item title="Lease Terms" value={listing.details.leaseTerms} />
      <Item
        title="Park Cost Monthly"
        value={listing.details.parkCostMonthly?.toString()}
      />
      <Item title="Family Room" value={listing.details.familyRoom} />
      <Item title="Central AC" value={listing.details.centralAirConditioning} />
      <Item title="Sewer" value={listing.details.sewer} />
      <Item
        title="Common Elements Incl."
        value={listing.details.commonElementsIncluded}
      />

      {listing.isCondo() ? (
        <>
          <Item
            title="Maintenance Fees"
            value={listing.condo.maintenanceHeader()}
          />
          <Item title="Exposure" value={listing.condo.exposureDirection()} />
          <Item title="Locker" value={listing.condo.locker} />
          <Item
            title="Locker Level"
            value={listing.condo.lockerLevel?.toString()}
          />
          <Item
            title="Locker Unit Number"
            value={listing.condo.lockerUnitNumber?.toString()}
          />
          <Item
            title="Locker Number"
            value={listing.condo.lockerNumber?.toString()}
          />
          <Item title="Parking Type" value={listing.condo.parkingType} />
          <Item title="Pets" value={listing.condo.pets} />
          <Item
            title="Property Manager"
            value={listing.condo.propertyManager}
          />
          <Item
            title="Building # Stories"
            value={listing.condo.stories?.toString()}
          />
          <Item
            title="Ensuite Laundry"
            value={listing.condo.hasEnsuiteLaundry}
          />
        </>
      ) : null}
    </div>
  );
}
